import { CardPopular } from '@blocks/cards-comparator-light/components/card-popular'
import { Divider } from '@ui/atoms/Divider'
import { CtaLink } from '@ui/cta-link'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { Segment } from '@ui/segment/segment'
import clsx from 'clsx'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { type FC, useCallback, useState } from 'react'

import { useIsIframe } from '../../../../plugins/gatsby-plugin-espace-client'
import {
  fosfo,
  fosfoShared,
  gold,
  goldShared,
  worldElite,
  worldEliteShared,
} from './fortuneo-cards-data'

const ComparatorLight: FC<{ isShared?: boolean }> = ({ isShared = false }) => {
  const { legalMention, ...headerAssets } =
    useStaticQuery<Queries.CardComparatorLightQuery>(graphql`
      fragment CardComparatorLightAsset on ContentfulAsset {
        description
        gatsbyImageData(width: 300, formats: [AVIF, WEBP, AUTO])
      }

      query CardComparatorLight {
        fosfo: contentfulAsset(
          contentful_id: { eq: "3HVxwbZXxbHe0favncWdml" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorLightAsset
        }
        gold: contentfulAsset(
          contentful_id: { eq: "1aJA4Xj4Bfv9pyvMLYYzFz" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorLightAsset
        }
        worldElite: contentfulAsset(
          contentful_id: { eq: "5hSxnbysPd66HOL1BQlxDq" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...CardComparatorLightAsset
        }
        legalMention: contentfulFootnote(
          contentful_id: { eq: "5wGDGpJCu5HIl7LWaaLFV8" }
          metadata: {
            tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
          }
        ) {
          ...ContentfulFootnoteSup
        }
      }
    `)
  const [selectedSegment, setSelectedSegment] = useState(
    gold(legalMention).mobileMenu.id
  )

  const cardsData = [
    fosfo(legalMention),
    gold(legalMention),
    worldElite(legalMention),
  ]
  const cardsSharedData = [
    fosfoShared(legalMention),
    goldShared(legalMention),
    worldEliteShared(legalMention),
  ]
  const data = isShared ? cardsSharedData : cardsData
  const isIframe = useIsIframe()

  const handleSegmentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedSegment(event.target.value)
    },
    []
  )

  return (
    <div className="cards-comparator relative flex flex-col items-center">
      <div className="backdrops-blur-lg sticky top-16 z-20 flex w-dvw justify-center px-5 py-4 laptop:hidden">
        <Segment
          name="segment-mobile-cards-comparator"
          selected={selectedSegment}
          items={data.map(({ mobileMenu: { id, label } }) => ({
            label,
            value: id,
          }))}
          onChange={handleSegmentChange}
        />
      </div>
      <div className="grid grid-cols-1 gap-8 laptop:grid-cols-3">
        {data.map(({ general, mobileMenu, isPopular, advantages }) => {
          const asset =
            headerAssets[
              general.assetName as keyof Queries.CardComparatorAssetsQuery
            ]
          return (
            <div
              key={general.assetName}
              className={clsx(
                'group/card mx-auto animate-fade-in laptop:animate-none',
                !isPopular && 'laptop:mt-[42px]',
                mobileMenu.id === selectedSegment
                  ? 'block'
                  : 'hidden laptop:block',
                mobileMenu.id
              )}
            >
              <CardPopular isPopular={isPopular}>
                <div
                  className={clsx(
                    'z-10 max-w-[325px] rounded-[0.625rem] border border-solid border-theme-default-accent2 bg-background-default p-6 pb-8 shadow drop-shadow-ds laptop:max-w-[350px]'
                  )}
                >
                  <GatsbyImage
                    alt={asset.description}
                    className="rounded-md"
                    image={getImage(asset)}
                  />
                  <h3 className="mb-2 mt-8 text-165">{general.name}</h3>
                  <p className="text-085">{general.underCondition}</p>

                  <div className="divide-y-small flex flex-col divide-solid divide-theme-default-accent2 text-165">
                    {advantages.map(({ Icon, content }) => (
                      <div className="flex items-center gap-4 py-8">
                        <div
                          className={clsx(
                            'rounded-md p-[6px] pt-[2px]',
                            'group-[.fosfo]/card:bg-theme-info-accent1',
                            'group-[.gold]/card:bg-[#D7A944] group-[.gold]/card:bg-opacity-10',
                            'group-[.world-elite]/card:bg-[#232323] group-[.world-elite]/card:bg-opacity-5'
                          )}
                        >
                          <Icon
                            className={clsx(
                              'inline-block h-[1em] w-[1em] shrink-0 select-none text-150',
                              'group-[.fosfo]/card:fill-theme-secondary-main',
                              'group-[.gold]/card:fill-[#D7A944]',
                              'group-[.world-elite]/card:fill-[#232323]'
                            )}
                          />
                        </div>
                        {content}
                      </div>
                    ))}
                  </div>
                  <Divider />
                  <div className="mt-8 flex justify-center">
                    <CtaLink
                      data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
                      data-type={ENHANCED_COMPONENT_TYPE.CTA}
                      href={isIframe ? general.clientLink : general.link}
                    >
                      Découvrir la carte {mobileMenu.label}
                    </CtaLink>
                  </div>
                </div>
              </CardPopular>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ComparatorLight
